import { useEffect } from 'react';
import type { BalanceEvent, LedgerKind } from '@tg/shared';
import { useSession } from '@/stores/session';
import { coinFly, type Point } from '@/fx/motion';
import { sound } from '@/fx/sound';

/**
 * Wallet display logic: turns server `wallet:balance` events into header animations.
 *
 * Every event updates `session.balance` (truth) immediately. What the header odometer shows
 * (`session.displayBalance`) is decided by a policy:
 *   - 'fly'   coins fly from `from` into the header, then the number rolls (teal flash)
 *   - 'quiet' the number rolls without coins or sound (bets, refunds, wins smaller than the bet)
 *   - 'hold'  keep showing the old number until the game calls wallet.reveal() after its own animation
 *             (slots: the spin result arrives before the reels stop)
 *
 * Default: positive win / daily bonus / prizes → 'fly'; everything else → 'quiet'.
 * A game overrides it while mounted:
 *   useBalancePolicy((e) => e.reason === 'win' ? { mode: 'hold' } : undefined);
 *   …after the win animation: wallet.reveal({ from: winEl, celebrate: win >= bet });
 */
export type BalanceFxMode = 'fly' | 'quiet' | 'hold';

export interface BalanceFxDecision {
  mode: BalanceFxMode;
  from?: Element | Point | null;
  coins?: number;
}

export type BalancePolicy = (e: BalanceEvent) => BalanceFxDecision | undefined;

const CELEBRATE: ReadonlySet<LedgerKind> = new Set<LedgerKind>([
  'win',
  'daily_bonus',
  'quest_reward',
  'tournament_prize',
  'invite_bonus',
  'signup_bonus',
]);

export const defaultBalancePolicy = (e: BalanceEvent): BalanceFxDecision =>
  e.delta > 0 && CELEBRATE.has(e.reason) ? { mode: 'fly' } : { mode: 'quiet' };

const policies: BalancePolicy[] = [];
const targets = new Set<Element>();
let holding = false;

function visible(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function defaultSource(): Point {
  return { x: innerWidth / 2, y: innerHeight / 2 };
}

async function animateTo(balance: number, d: BalanceFxDecision): Promise<void> {
  const s = useSession.getState();
  if (d.mode === 'fly' && balance > s.displayBalance) {
    const target = wallet.target();
    if (target) {
      // one recorded stream of coins instead of a plink per coin ('fly' = a win not smaller than the bet)
      sound.play('common/coins');
      await coinFly(d.from ?? defaultSource(), target, d.coins ?? 10, { sound: false });
    }
  }
  // the odometer reads displayBalance and rolls; its flash colour follows the sign of the change
  useSession.getState().setDisplayBalance(useSession.getState().balance);
}

export const wallet = {
  /** Apply a server balance push (socket `wallet:balance`, or an HTTP response that returns a balance). */
  receive(e: BalanceEvent): Promise<void> {
    const s = useSession.getState();
    if (s.status !== 'user') return Promise.resolve();
    if (e.balance === s.balance && !holding) {
      if (s.displayBalance !== e.balance) s.setDisplayBalance(e.balance);
      return Promise.resolve();
    }
    s.setBalance(e.balance);
    let d: BalanceFxDecision | undefined;
    for (let i = policies.length - 1; i >= 0 && !d; i--) d = policies[i]?.(e);
    d ??= defaultBalancePolicy(e);
    if (d.mode === 'hold') {
      holding = true;
      return Promise.resolve();
    }
    if (holding) return Promise.resolve(); // a held credit is revealed by the game
    return animateTo(e.balance, d);
  },

  /** Release a held balance: roll the header to the real balance, optionally with coins from `from`. */
  reveal(o: { from?: Element | Point | null; celebrate?: boolean; coins?: number } = {}): Promise<void> {
    holding = false;
    return animateTo(useSession.getState().balance, {
      mode: o.celebrate ? 'fly' : 'quiet',
      from: o.from ?? null,
      coins: o.coins,
    });
  },

  get holding(): boolean {
    return holding;
  },

  /** The visible header balance element (coin-fly destination). */
  target(): Element | null {
    for (const el of targets) if (el.isConnected && visible(el)) return el;
    for (const el of targets) if (el.isConnected) return el;
    return null;
  },

  registerTarget(el: Element): () => void {
    targets.add(el);
    return () => targets.delete(el);
  },

  pushPolicy(p: BalancePolicy): () => void {
    policies.push(p);
    return () => {
      const i = policies.lastIndexOf(p);
      if (i >= 0) policies.splice(i, 1);
      if (!policies.length && holding) void wallet.reveal();
    };
  },
};

/** Register a balance policy while the component is mounted (keep `policy` stable with useCallback). */
export function useBalancePolicy(policy: BalancePolicy): void {
  useEffect(() => wallet.pushPolicy(policy), [policy]);
}
